/**
 * The package's ENGLISH copy — the standalone default, not the locale layer.
 *
 * Byte-identical to the iOS Feedback catalogue
 * (`MetroModules/Sources/Feedback/Localizable.xcstrings`) source values.
 *
 * Like the other `@metro/*` packages, this one is locale-agnostic: it takes
 * its copy as a `strings` prop and ships this set so it renders standalone.
 * The metro-frontend host owns the locale layer — it reads the `feedback`
 * i18next namespace and builds the set in `src/i18n/packageStrings.ts`. The
 * catalogue keys are the SAME names iOS and Android use, so the three
 * platforms stay diffable key by key.
 */
/**
 * The copy the package needs. {@link FeedbackStrings} is the English
 * instance; the host passes a localized one built from the `feedback`
 * catalogue. Declared explicitly (rather than `typeof` an `as const`
 * literal) so any string fits — mirrors `RouteDetailsStringSet`.
 */
export interface FeedbackStringSet {
  readonly submit: string
  readonly notNow: string
  readonly notePlaceholder: string
  readonly thankYouTitle: string
  readonly thankYouDescription: string
  readonly route: {
    readonly title: string
    readonly footer: string
    readonly categories: FeedbackRouteCategories
    readonly reasons: FeedbackRouteReasons
  }
  readonly routeDetails: {
    readonly title: string
    readonly categories: FeedbackRouteCategories
    readonly reasons: FeedbackRouteReasons
  }
  readonly stationDetails: {
    readonly title: string
    readonly categories: {
      readonly stationInfo: string
      readonly mapAndNavigation: string
      readonly appExperience: string
    }
    readonly reasons: {
      readonly incorrectInfo: string
      readonly missingInfo: string
      readonly wrongLocation: string
      readonly hardToFind: string
      readonly incorrectMapInfo: string
      readonly slowOrBuggy: string
      readonly confusingInterface: string
    }
  }
}

/** Shared by the `route` and `routeDetails` forms — identical category set. */
export interface FeedbackRouteCategories {
  readonly routeAccuracy: string
  readonly mapAndNavigation: string
  readonly appExperience: string
}

/** Shared by the `route` and `routeDetails` forms — identical reason set. */
export interface FeedbackRouteReasons {
  readonly wrongRoute: string
  readonly tooManyTransfers: string
  readonly timingWasWrong: string
  readonly wrongDirection: string
  readonly hardToUse: string
  readonly hardToFindStations: string
  readonly incorrectMapInfo: string
  readonly slowOrBuggy: string
  readonly confusingInterface: string
}

export const FeedbackStrings: FeedbackStringSet = {
  submit: 'Submit',
  notNow: 'Not now',
  notePlaceholder: 'Anything to add?',
  thankYouTitle: 'Thanks for your\nfeedback!',
  thankYouDescription: 'We read every report to make the app better',

  route: {
    title: 'What went wrong?',
    footer: 'Thanks! This helps us improve routes',
    categories: {
      routeAccuracy: 'Route accuracy',
      mapAndNavigation: 'Map & navigation',
      appExperience: 'App experience',
    },
    reasons: {
      wrongRoute: 'Wrong route',
      tooManyTransfers: 'Too many transfers',
      timingWasWrong: 'Timing was wrong',
      wrongDirection: 'Wrong direction',
      hardToUse: 'Hard to use',
      hardToFindStations: 'Hard to find stations',
      incorrectMapInfo: 'Incorrect map info',
      slowOrBuggy: 'Slow or buggy',
      confusingInterface: 'Confusing interface',
    },
  },

  routeDetails: {
    title: "What didn't work\nfor you?",
    categories: {
      routeAccuracy: 'Route accuracy',
      mapAndNavigation: 'Map & navigation',
      appExperience: 'App experience',
    },
    reasons: {
      wrongRoute: 'Wrong route',
      tooManyTransfers: 'Too many transfers',
      timingWasWrong: 'Timing was wrong',
      wrongDirection: 'Wrong direction',
      hardToUse: 'Hard to use',
      hardToFindStations: 'Hard to find stations',
      incorrectMapInfo: 'Incorrect map info',
      slowOrBuggy: 'Slow or buggy',
      confusingInterface: 'Confusing interface',
    },
  },

  stationDetails: {
    title: "What didn't work\nfor you?",
    categories: {
      stationInfo: 'Station info',
      mapAndNavigation: 'Map & navigation',
      appExperience: 'App experience',
    },
    reasons: {
      incorrectInfo: 'Incorrect info',
      missingInfo: 'Missing info',
      wrongLocation: 'Wrong location',
      hardToFind: 'Hard to find',
      incorrectMapInfo: 'Incorrect map info',
      slowOrBuggy: 'Slow or buggy',
      confusingInterface: 'Confusing interface',
    },
  },
}
